// QuestionButton.js
import React, { useState, useRef, useEffect } from "react";
import {
  FaLightbulb,
  FaCamera,
  FaImage,
  FaTimes,
  FaPaperPlane,
  FaCheck,
  FaRegSmile,
  FaEnvelope
} from "react-icons/fa";
import "./QuestionButton.css";
import axios from "axios";

const QuestionButton = () => {
  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [email, setEmail] = useState(""); // New email state
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isPulsing, setIsPulsing] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [modalAnimation, setModalAnimation] = useState("");

  // Refs
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  // Handle pulse animation on component mount
  useEffect(() => {
    let pulseCount = 0;
    const pulseInterval = setInterval(() => {
      pulseCount++;
      if (pulseCount >= 5) {
        setIsPulsing(false);
        clearInterval(pulseInterval);
      }
    }, 2000);

    return () => clearInterval(pulseInterval);
  }, []);

  // Handle escape key and animation
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        closeModal();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";

      // Start animation
      setTimeout(() => {
        setModalAnimation("modal-visible");
      }, 10);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Open modal
  const openModal = () => {
    setIsOpen(true);
    setIsPulsing(false);
  };

  // Close modal with animation
  const closeModal = () => {
    setModalAnimation("modal-exit");

    setTimeout(() => {
      setIsOpen(false);
      resetForm();
      document.body.style.overflow = "auto";
    }, 300);
  };

  // Handle clicking outside modal
  const handleOutsideClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      closeModal();
    }
  };

  // Question input handler
  const handleQuestionChange = (e) => {
    setQuestion(e.target.value);
  };

  // Email input handler
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  // File processing
  const processFile = (file) => {
    if (!file) return;

    // Image type validation
    if (!file.type.match("image.*")) {
      alert("Please select an image file (jpg, png, etc)");
      return;
    }

    // Size validation (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert("Please select an image smaller than 10MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Image upload handler
  const handleImageUpload = (e) => {
    processFile(e.target.files[0]);
  };

  // Drag & drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  // Open file selector
  const openFileSelector = () => {
    fileInputRef.current.click();
  };

  // Open camera
  const openCamera = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      fileInputRef.current.setAttribute("capture", "environment");
      fileInputRef.current.click();

      setTimeout(() => {
        fileInputRef.current.removeAttribute("capture");
      }, 1000);
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Could not access your camera. Please check permissions.");
    }
  };

  // Remove image
  const removeImage = () => {
    setImagePreview(null);
  };

  // Reset form
  const resetForm = () => {
    setQuestion("");
    setEmail("");
    setImagePreview(null);
    setSubmitted(false);
  };

  // Submit question
  const submitQuestion = async () => {
    if ((!question && !imagePreview) || !email) return;

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('question', question);
      formData.append('email', email); // Add email to form data

      if (imagePreview) {
        // Convert base64 to blob and append
        const blob = await fetch(imagePreview).then(r => r.blob());
        formData.append('image', blob, 'image.jpg');
      }

      // Send to your Laravel API
      const response = await axios.post('https://num8ers.cloud/api/submit-question', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      console.log("Question submitted:", response.data);
      setSubmitted(true);

      // Close after showing success message
      setTimeout(() => {
        closeModal();
      }, 3000);
    } catch (error) {
      console.error("Error submitting question:", error);
      alert("There was an error submitting your question. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="qb-container">
      {/* Floating Button */}
      <button
        className={`qb-floating-button ${isOpen ? "open" : ""} ${isPulsing ? "pulse" : ""
          }`}
        onClick={openModal}
        aria-label="Ask a question"
      >
        {isOpen ? <FaTimes /> : <FaLightbulb />}
        {!isOpen && <div className="qb-tooltip">Have a question?</div>}
      </button>

      {/* Modal */}
      {isOpen && (
        <div
          className={`qb-backdrop ${modalAnimation}`}
          onClick={handleOutsideClick}
          aria-modal="true"
          role="dialog"
        >
          <div
            className={`qb-modal ${modalAnimation}`}
            onClick={(e) => e.stopPropagation()}
            ref={modalRef}
          >
            {/* Close Button */}
            <button
              className="qb-close-button"
              onClick={closeModal}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            {/* Success Screen */}
            {submitted ? (
              <div className="qb-success">
                <div className="qb-success-icon">
                  <FaCheck />
                </div>
                <h3>Thank you!</h3>
                <p>
                  We've received your question and will work on a solution soon.
                </p>
                <FaRegSmile className="qb-smile" />
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="qb-header">
                  <div className="qb-icon-circle">
                    <FaLightbulb />
                  </div>
                  <h3 className="qb-title">
                    Have a question for us to solve? Tell us
                  </h3>
                </div>

                {/* Form */}
                <div
                  className={`qb-form ${dragActive ? "drag-active" : ""}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {/* Question Input */}
                  <div className="qb-question-group">
                    <div className="qb-accent-line"></div>
                    <label htmlFor="question-input">Your Question</label>
                    <textarea
                      id="question-input"
                      className="qb-textarea"
                      placeholder="Type your question here..."
                      value={question}
                      onChange={handleQuestionChange}
                      rows={4}
                    />
                  </div>

                  {/* Email Input */}
                  <div className="qb-question-group">
                    <div className="qb-accent-line"></div>
                    <label htmlFor="email-input">Your Email</label>
                    <input
                      id="email-input"
                      className="qb-textarea" // Reusing the textarea styling
                      type="email"
                      placeholder="Enter your email to receive updates..."
                      value={email}
                      onChange={handleEmailChange}
                      style={{ minHeight: 'auto', height: '50px' }} // Override height
                    />
                  </div>

                  {/* Drag Overlay */}
                  {dragActive && (
                    <div className="qb-drag-overlay">
                      <FaImage />
                      <p>Drop image here</p>
                    </div>
                  )}

                  {/* Image Preview or Upload Section */}
                  {imagePreview ? (
                    <div className="qb-preview">
                      <div className="qb-preview-header">
                        <span>Image attached</span>
                        <button
                          className="qb-remove-button"
                          onClick={removeImage}
                          aria-label="Remove image"
                        >
                          <FaTimes />
                          <span>Remove</span>
                        </button>
                      </div>
                      <div className="qb-image-container">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="qb-image"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="qb-upload">
                      <p className="qb-upload-text">
                        Add an image to help explain your question
                      </p>
                      <div className="qb-buttons">
                        <button
                          className="qb-upload-button"
                          onClick={openFileSelector}
                          disabled={isSubmitting}
                          aria-label="Upload image"
                          title="Upload an image of your problem"
                        >
                          <div className="qb-button-icon upload-icon">
                            <FaImage />
                          </div>
                          <span>Upload Image</span>
                        </button>

                        <button
                          className="qb-camera-button"
                          onClick={openCamera}
                          disabled={isSubmitting}
                          aria-label="Take photo"
                          title="Take a photo of your problem"
                        >
                          <div className="qb-button-icon camera-icon">
                            <FaCamera />
                          </div>
                          <span>Use Camera</span>
                        </button>

                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                      </div>
                      <p className="qb-drag-text">
                        Or drag & drop an image here
                      </p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="qb-submit-wrapper">
                    <button
                      className={`qb-submit ${(!question && !imagePreview) || !email ? "disabled" : ""
                        }`}
                      onClick={submitQuestion}
                      disabled={(!question && !imagePreview) || !email || isSubmitting}
                      aria-label="Submit question"
                    >
                      {isSubmitting ? (
                        <span className="qb-spinner"></span>
                      ) : (
                        <>
                          <FaPaperPlane />
                          <span>Submit Question</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionButton;